import { useEffect, useRef, useState } from "react";
import { Button, Modal, Spin } from "antd";
import { CameraOutlined } from "@ant-design/icons";
import { ErrorAlert } from "@gull/ui";

const MAX_IMAGE_DIMENSION = 2048;

export function isCameraCaptureSupported() {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
}

function cameraErrorMessage(error: unknown) {
  if (error instanceof DOMException) {
    if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
      return "Kameratilgang ble ikke gitt. Tillat kamera i nettleseren for å ta bilde.";
    }
    if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
      return "Fant ikke noe kamera på denne enheten.";
    }
    if (error.name === "NotReadableError" || error.name === "TrackStartError") {
      return "Kameraet er opptatt eller kunne ikke startes akkurat nå.";
    }
    if (error.name === "OverconstrainedError") {
      return "Kameraet støtter ikke denne visningen. Prøv igjen.";
    }
  }
  return "Kunne ikke åpne kameraet.";
}

export default function CameraCapture({
  open,
  onCancel,
  onCapture,
}: {
  open: boolean;
  onCancel: () => void;
  onCapture: (file: File) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [booting, setBooting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [captured, setCaptured] = useState<File | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setBooting(true);

    let acquired: MediaStream | null = null;

    void (async () => {
      try {
        const next = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) {
          next.getTracks().forEach((track) => track.stop());
          return;
        }
        acquired = next;
        setStream(next);
        setBooting(false);
      } catch (cameraError) {
        if (cancelled) return;
        setBooting(false);
        setError(cameraErrorMessage(cameraError));
      }
    })();

    return () => {
      cancelled = true;
      acquired?.getTracks().forEach((track) => track.stop());
      setStream(null);
    };
  }, [open]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) return;
    video.srcObject = stream;
    void video.play().catch(() => undefined);
  }, [stream, previewUrl]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const discardPreview = () => {
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
    setCaptured(null);
  };

  const close = () => {
    discardPreview();
    onCancel();
  };

  const takePhoto = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.videoWidth === 0 || video.videoHeight === 0) {
      setError("Kamerabildet er ikke klart ennå. Vent et øyeblikk og prøv igjen.");
      return;
    }

    let width = video.videoWidth;
    let height = video.videoHeight;
    if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
      const scale = MAX_IMAGE_DIMENSION / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }

    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      setError("Kunne ikke lagre bildet fra kameraet.");
      return;
    }
    context.drawImage(video, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((result) => resolve(result), "image/jpeg", 0.88);
    });
    if (!blob) {
      setError("Kunne ikke lagre bildet fra kameraet.");
      return;
    }

    const file = new File([blob], `kamera-${Date.now()}.jpg`, { type: "image/jpeg" });
    setCaptured(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const usePhoto = () => {
    if (!captured) return;
    onCapture(captured);
    discardPreview();
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      width={720}
      destroyOnHidden
      title="Ta et bilde"
      className="sendin-camera-modal"
    >
      <ErrorAlert error={error} style={{ marginBottom: 16 }} />

      <div className="sendin-camera-stage">
        {previewUrl ? (
          <img src={previewUrl} alt="Bildet du tok" />
        ) : (
          <>
            <video ref={videoRef} autoPlay playsInline muted />
            {booting ? <Spin className="sendin-camera-spinner" /> : null}
          </>
        )}
      </div>
      <canvas ref={canvasRef} hidden />

      <div className="sendin-camera-actions">
        {previewUrl ? (
          <>
            <Button className="sendin-secondary-button" onClick={discardPreview}>
              Ta bilde på nytt
            </Button>
            <Button className="sendin-black-button" onClick={usePhoto}>
              Bruk bildet
            </Button>
          </>
        ) : (
          <>
            <Button className="sendin-secondary-button" onClick={close}>
              Avbryt
            </Button>
            <Button
              className="sendin-black-button"
              icon={<CameraOutlined />}
              iconPosition="end"
              disabled={!stream || !!error}
              onClick={() => void takePhoto()}
            >
              Ta bilde
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}
